/**
 * Where every glyph of a text layer goes. §6.4.
 *
 * Straight, bent along an arc, or laid along a drawn line. One answer, read by
 * the canvas and by both press outputs, for the reason everything in this
 * project is shared: a curve worked out twice is two curves, and the customer
 * approves one and receives the other.
 *
 * Each glyph comes back as its outline - untouched, in font units, y up - and
 * a matrix that puts it in the layer's own millimetres. Writing the matrix
 * rather than moving the outline keeps a long glyph path exact, and lets PDF
 * and SVG each say it their own way.
 */

import type { TextLayer } from '../model/types';
import type { ShapedParagraph } from './shaper';
import { ptToMm } from '../model/units';
import { freePath } from '../shapes/library';

/** An affine map, as SVG and PDF both write it: x' = a x + c y + e, y' = b x + d y + f. */
export type Matrix = [ number, number, number, number, number, number ];

export interface PlacedGlyph {
  /** The outline, in font units, y up. */
  d: string;
  /** From font units to the layer's millimetres. */
  m: Matrix;
  /** How far the glyph advances, in font units - its width as a piece of type. */
  advance: number;
}

export interface Placement {
  glyphs: PlacedGlyph[];
  /** Underline and strike-through rules, in millimetres. Straight text only. */
  rules: Array<{ x: number; y: number; w: number; h: number }>;
  /**
   * What the text actually covers, in the layer's millimetres. For straight
   * text this is the layout's own box; bent text covers whatever the bend
   * makes, and the layer box is fitted to this when the curve changes.
   */
  extent: { x: number; y: number; w: number; h: number };
}

interface Laid {
  paragraph: ShapedParagraph;
  unitsPerMm: number;
}

/** At a curve of 100 the longest line wraps this far round - not quite a circle, so the ends do not collide. */
const MOST_TURN = Math.PI * 2 * 0.92;

export function placeText( layer: TextLayer, laid: Laid, straight = false ): Placement {
  if ( ! straight && layer.path && layer.path.nodes.length >= 2 ) return alongPath( layer, laid );
  if ( ! straight && layer.curve && Math.abs( layer.curve ) >= 1 ) return onArc( layer, laid );

  return rows( layer, laid );
}

/** Whether a layer's glyphs are anywhere but on straight rows. */
export function isBent( layer: TextLayer ): boolean {
  return !! ( ( layer.path && layer.path.nodes.length >= 2 ) || ( layer.curve && Math.abs( layer.curve ) >= 1 ) );
}

/* ---------------------------------------------------------------
 * Straight
 * ------------------------------------------------------------ */

function rows( layer: TextLayer, { paragraph, unitsPerMm }: Laid ): Placement {
  const s = 1 / unitsPerMm;
  const glyphs: PlacedGlyph[] = [];
  const rules: Placement[ 'rules' ] = [];

  /*
   * Underline and strike-through, from proportions of the type size, so they
   * hold at 6 pt and at 60. Filled rectangles rather than stroked lines: a
   * stroke is centred on its path, so half of it would sit above where it was
   * meant to - and on a press file that is a line touching the descenders.
   */
  const sizeMm = ptToMm( layer.font.size );
  const rule = Math.max( 0.05, sizeMm * 0.06 );

  let baseline = paragraph.ascender * s;

  for ( const line of paragraph.lines ) {
    for ( const glyph of line.glyphs ) {
      if ( glyph.path === '' ) continue;

      // Flipped about the glyph's own origin: outlines are y up, the page is y down.
      glyphs.push( {
        d: glyph.path,
        m: [ s, 0, 0, -s, ( line.offset + glyph.x ) * s, baseline - glyph.y * s ],
        advance: glyph.advance,
      } );
    }

    if ( ( layer.underline || layer.strike ) && line.width > 0 ) {
      const from = line.offset * s;
      const width = line.width * s;

      if ( layer.underline ) rules.push( { x: from, y: baseline + sizeMm * 0.12, w: width, h: rule } );
      if ( layer.strike ) rules.push( { x: from, y: baseline - sizeMm * 0.26, w: width, h: rule } );
    }

    baseline += paragraph.leading * s;
  }

  return {
    glyphs,
    rules,
    extent: { x: 0, y: 0, w: layer.w > 0 ? layer.w : paragraph.width * s, h: paragraph.height * s },
  };
}

/* ---------------------------------------------------------------
 * On an arc
 * ------------------------------------------------------------ */

/**
 * Every line on its own ring of one circle, the rings a line-height apart.
 *
 * Each glyph is placed by its middle: its middle goes on the ring, and it is
 * turned to the ring's direction there. Placing by the left edge instead tips
 * every letter slightly forward, which reads as the text sliding downhill.
 */
function onArc( layer: TextLayer, { paragraph, unitsPerMm }: Laid ): Placement {
  const s = 1 / unitsPerMm;
  const curve = Math.max( -100, Math.min( 100, layer.curve ?? 0 ) );
  const up = curve > 0;

  const longest = Math.max( ...paragraph.lines.map( ( line ) => line.width * s ), 0.1 );
  const radius = longest / ( ( Math.abs( curve ) / 100 ) * MOST_TURN );

  /*
   * Every line bends about its own middle, whatever its alignment. Bending
   * about the middle of the box instead put left-aligned text on one side of
   * the arc - climbing steadily rather than arching - and the arch is the
   * whole point. Curved text is centred text, as in every tool that offers it.
   */
  const box = layer.w > 0 ? layer.w : paragraph.width * s;
  const first = paragraph.ascender * s;

  const glyphs: PlacedGlyph[] = [];

  paragraph.lines.forEach( ( line, index ) => {
    const baseline = first + index * paragraph.leading * s;
    const drop = baseline - first;
    // Lower lines sit on the inside of an arch and the outside of a smile.
    const ring = Math.max( 0.5, up ? radius - drop : radius + drop );

    for ( const glyph of line.glyphs ) {
      if ( glyph.path === '' ) continue;

      const along = ( glyph.x + glyph.advance / 2 - line.width / 2 ) * s;
      const angle = along / ring;
      const turn = up ? angle : -angle;

      const px = box / 2 + ring * Math.sin( angle );
      const py = up ? first + radius - ring * Math.cos( angle ) : first - radius + ring * Math.cos( angle );

      glyphs.push( {
        d: glyph.path,
        m: aroundPoint( px, py, turn, s, ( glyph.advance * s ) / 2, glyph.y * s ),
        advance: glyph.advance,
      } );
    }
  } );

  return centred( glyphs, layer, paragraph );
}

/**
 * A glyph's matrix: flipped upright, its middle on the point, turned.
 *
 *   point . rotate(turn) . move(-half, -rise) . scale(s, -s)
 */
function aroundPoint( px: number, py: number, turn: number, s: number, half: number, rise: number ): Matrix {
  const cos = Math.cos( turn );
  const sin = Math.sin( turn );
  const gx = -half;
  const gy = -rise;

  return [ s * cos, s * sin, s * sin, -s * cos, cos * gx - sin * gy + px, sin * gx + cos * gy + py ];
}

/**
 * Moves bent text so what it covers sits in the middle of the layer's box.
 *
 * An arch is taller than the line it was and a smile hangs lower, so without
 * this the text wanders out of its own box as the curve changes - and the box
 * is what the customer selects, snaps and aligns.
 */
function centred( glyphs: PlacedGlyph[], layer: TextLayer, paragraph: ShapedParagraph ): Placement {
  const covers = cover( glyphs, paragraph );

  if ( ! covers ) return { glyphs, rules: [], extent: { x: 0, y: 0, w: layer.w, h: layer.h } };

  const dx = layer.w / 2 - ( covers.x + covers.w / 2 );
  const dy = layer.h / 2 - ( covers.y + covers.h / 2 );

  return {
    glyphs: glyphs.map( ( g ) => ( {
      ...g,
      m: [ g.m[ 0 ], g.m[ 1 ], g.m[ 2 ], g.m[ 3 ], g.m[ 4 ] + dx, g.m[ 5 ] + dy ] as Matrix,
    } ) ),
    rules: [],
    extent: { x: covers.x + dx, y: covers.y + dy, w: covers.w, h: covers.h },
  };
}

/**
 * What a set of placed glyphs covers: each glyph's em box - advance wide,
 * ascender to descender tall - carried through its matrix. The em box rather
 * than the outline's own bounds, because it is what a line of type occupies
 * and it does not jump when a letter with a descender is typed.
 */
function cover( glyphs: PlacedGlyph[], paragraph: ShapedParagraph ) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for ( const g of glyphs ) {
    for ( const [ ux, uy ] of [
      [ 0, paragraph.ascender ],
      [ g.advance, paragraph.ascender ],
      [ 0, paragraph.descender ],
      [ g.advance, paragraph.descender ],
    ] as Array<[ number, number ]> ) {
      const x = g.m[ 0 ] * ux + g.m[ 2 ] * uy + g.m[ 4 ];
      const y = g.m[ 1 ] * ux + g.m[ 3 ] * uy + g.m[ 5 ];

      minX = Math.min( minX, x );
      minY = Math.min( minY, y );
      maxX = Math.max( maxX, x );
      maxY = Math.max( maxY, y );
    }
  }

  return Number.isFinite( minX ) ? { x: minX, y: minY, w: maxX - minX, h: maxY - minY } : null;
}

/* ---------------------------------------------------------------
 * Along a drawn line
 * ------------------------------------------------------------ */

/**
 * The text walks the line: each glyph's middle at its distance along it,
 * turned to the line's direction there, its baseline on the line.
 *
 * One run of text - line breaks become spaces, because a line has one
 * direction to be read in. What does not fit is not drawn, as in every other
 * tool that does this; the panel says so.
 */
function alongPath( layer: TextLayer, { paragraph, unitsPerMm }: Laid ): Placement {
  const s = 1 / unitsPerMm;
  const route = polyline( layer );
  const total = route.length ? ( route[ route.length - 1 ] as Station ).at : 0;

  // The glyphs in reading order, each with its distance from the run's start.
  const gap = paragraph.unitsPerEm * 0.3;
  const run: Array<{ d: string; centre: number; half: number; rise: number; advance: number }> = [];
  let pen = 0;

  for ( const line of paragraph.lines ) {
    for ( const glyph of line.glyphs ) {
      if ( glyph.path !== '' ) {
        run.push( {
          d: glyph.path,
          // glyph.x runs from the line's own start; the alignment offset is
          // left out, because along a path the path decides where text starts.
          centre: ( pen + glyph.x + glyph.advance / 2 ) * s,
          half: ( glyph.advance * s ) / 2,
          rise: glyph.y * s,
          advance: glyph.advance,
        } );
      }
    }

    pen += line.width + gap;
  }

  const width = Math.max( 0, pen - gap ) * s;
  const start = layer.align === 'center' ? ( total - width ) / 2 : layer.align === 'right' ? total - width : 0;

  const glyphs: PlacedGlyph[] = [];

  for ( const item of run ) {
    const at = start + item.centre;

    if ( at < 0 || at > total ) continue;

    const where = pointAt( route, at );

    glyphs.push( {
      d: item.d,
      m: aroundPoint( where.x, where.y, where.angle, s, item.half, item.rise ),
      advance: item.advance,
    } );
  }

  const covers = cover( glyphs, paragraph );

  return {
    glyphs,
    rules: [],
    extent: covers ?? { x: 0, y: 0, w: layer.w, h: layer.h },
  };
}

interface Station {
  x: number;
  y: number;
  /** Distance from the start, in millimetres. */
  at: number;
}

/** The line as short straight steps, with the distance run so far at each. */
function polyline( layer: TextLayer ): Station[] {
  const path = layer.path;

  if ( ! path ) return [];

  // freePath already knows how to turn the stored points into a curve; asking
  // it for millimetres and walking the result keeps one definition of the line.
  const d = freePath( path.nodes, path.closed, layer.w, layer.h );
  const out: Station[] = [];
  const tokens = d.match( /[MLCZ][^MLCZ]*/g ) ?? [];
  let x = 0;
  let y = 0;
  let startX = 0;
  let startY = 0;

  const step = ( nx: number, ny: number ) => {
    const last = out[ out.length - 1 ];

    out.push( { x: nx, y: ny, at: last ? last.at + Math.hypot( nx - last.x, ny - last.y ) : 0 } );
    x = nx;
    y = ny;
  };

  for ( const token of tokens ) {
    const n = ( token.slice( 1 ).match( /-?[\d.]+(?:e-?\d+)?/gi ) ?? [] ).map( Number );

    switch ( token[ 0 ] ) {
      case 'M':
        startX = n[ 0 ] ?? 0;
        startY = n[ 1 ] ?? 0;
        step( startX, startY );
        break;
      case 'L':
        step( n[ 0 ] ?? 0, n[ 1 ] ?? 0 );
        break;
      case 'C': {
        const [ x1 = 0, y1 = 0, x2 = 0, y2 = 0, x3 = 0, y3 = 0 ] = n;
        const x0 = x;
        const y0 = y;

        for ( let i = 1; i <= 24; i++ ) {
          const t = i / 24;
          const u = 1 - t;

          step(
            u * u * u * x0 + 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t * x3,
            u * u * u * y0 + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * y3
          );
        }
        break;
      }
      case 'Z':
        step( startX, startY );
        break;
      default:
    }
  }

  return out;
}

/** A point at a distance along the steps, and the direction of travel there. */
function pointAt( route: Station[], at: number ): { x: number; y: number; angle: number } {
  for ( let i = 1; i < route.length; i++ ) {
    const a = route[ i - 1 ] as Station;
    const b = route[ i ] as Station;

    if ( at <= b.at || i === route.length - 1 ) {
      const span = b.at - a.at || 1;
      const t = Math.min( 1, Math.max( 0, ( at - a.at ) / span ) );

      return { x: a.x + ( b.x - a.x ) * t, y: a.y + ( b.y - a.y ) * t, angle: Math.atan2( b.y - a.y, b.x - a.x ) };
    }
  }

  const only = route[ 0 ] ?? { x: 0, y: 0 };

  return { x: only.x, y: only.y, angle: 0 };
}
